/*
 * itv-x-post-format.js — X/Twitter fidelity primitives
 *
 * Kept OUT of itv-x-post-app.js on purpose. Everything here is a pure function
 * of its arguments, which means it can be unit-tested in node with no DOM and
 * no theme (see rebuild/tools/test-x-post-format.mjs). The app file cannot be
 * tested that way, so mixing the two would cost us the only part of this tool
 * with real algorithmic content that is cheap to prove correct.
 *
 * These are the details competing generators get wrong. The SERP tool's
 * equivalent is its live pixel-width truncation counter mirroring Google's
 * typography: not a feature anyone asks for by name, and the reason the output
 * looks real instead of looking close.
 *
 * No UI strings live here. Anything user-visible is passed in by the caller
 * from the i18n pack, because this file is also used under /ru/ and /es/.
 */
(function (window) {
    'use strict';

    /* =====================================================================
     * 1. Weighted character counting
     * =====================================================================
     *
     * X does NOT count characters. It counts WEIGHTED units, per the
     * twitter-text v3 configuration, and this is the single most-missed rule
     * in the category — every competitor surveyed counts `text.length`.
     *
     *   - limit is 280 UNITS, not 280 characters
     *   - codepoints in the ranges below weigh 100; everything else weighs 200
     *   - the sum is divided by scale (100), so Latin costs 1 and CJK costs 2
     *   - a URL costs exactly 23 REGARDLESS OF LENGTH, because t.co rewrites it
     *   - an emoji costs 2 even when it is a ZWJ sequence of six codepoints
     *
     * The URL rule is the one that visibly changes the counter: a 120-character
     * link costs 23. Get it wrong and the counter is off by ~100 on any post
     * containing a link, which is most of them.
     */

    var CFG = {
        version: 3,
        maxWeightedLength: 280,
        scale: 100,
        defaultWeight: 200,
        transformedURLLength: 23,
        // Ranges that cost HALF weight. Everything outside them costs full.
        // These are the twitter-text v3 ranges verbatim.
        ranges: [
            { start: 0, end: 4351, weight: 100 },
            { start: 8192, end: 8205, weight: 100 },
            { start: 8208, end: 8223, weight: 100 },
            { start: 8242, end: 8247, weight: 100 }
        ]
    };

    /*
     * URL extraction.
     *
     * Deliberately a close approximation, not a reimplementation of
     * twitter-text's extractor — that is ~400 lines of TLD tables. This
     * matches what a person actually pastes: a scheme-ful URL, or a bare
     * host with a known-shaped TLD. Being slightly conservative is the safe
     * direction: an unrecognised URL is counted as its literal characters, so
     * the counter reads HIGHER than reality and the user is never told they
     * fit when they do not.
     */
    var URL_RE = /(?:https?:\/\/[^\s<>"']+)|(?:(?:^|[\s(])(?:www\.)?[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)*\.(?:com|org|net|edu|gov|mil|int|io|co|ai|app|dev|me|tv|uk|ru|es|de|fr|jp|cn|br|in|au|ca|nl|se|no|fi|pl|it|info|biz|xyz|online|site|shop|store|tech|news|blog)(?:\/[^\s<>"']*)?)/gi;

    /*
     * Emoji detection.
     *
     * An emoji is ONE weighted unit even when it is a ZWJ sequence — the
     * family emoji is seven codepoints and costs 2, not 14. Prefer the v-flag
     * RGI_Emoji property where the engine has it; fall back to an explicit
     * sequence pattern otherwise. Feature-detected at load, not assumed:
     * Safari shipped `v` late and this tool runs on whatever the visitor has.
     */
    var EMOJI_RE = null;
    try {
        EMOJI_RE = new RegExp('\\p{RGI_Emoji}', 'gv');
    } catch (e) {
        try {
            // Extended_Pictographic, optionally with modifiers/VS16, joined by ZWJ.
            EMOJI_RE = new RegExp(
                '\\p{Extended_Pictographic}(?:\\p{Emoji_Modifier}|\\uFE0F)?' +
                '(?:\\u200D\\p{Extended_Pictographic}(?:\\p{Emoji_Modifier}|\\uFE0F)?)*' +
                '|[\\u{1F1E6}-\\u{1F1FF}]{2}',
                'gu'
            );
        } catch (e2) {
            EMOJI_RE = null;   // ancient engine: emoji fall through to codepoints
        }
    }

    function codepointWeight(cp) {
        for (var i = 0; i < CFG.ranges.length; i++) {
            var r = CFG.ranges[i];
            if (cp >= r.start && cp <= r.end) return r.weight;
        }
        return CFG.defaultWeight;
    }

    /* Weight of a run of text that is known to contain no URLs. */
    function weighRun(run) {
        if (!run) return 0;
        var total = 0;
        var idx = 0;

        // Walk emoji matches and the plain gaps between them, in order.
        if (EMOJI_RE) {
            EMOJI_RE.lastIndex = 0;
            var m;
            while ((m = EMOJI_RE.exec(run)) !== null) {
                if (m.index > idx) total += weighPlain(run.slice(idx, m.index));
                total += CFG.defaultWeight;          // whole sequence = one unit of 2
                idx = m.index + m[0].length;
                if (m[0].length === 0) EMOJI_RE.lastIndex++;   // guard: never spin
            }
        }
        if (idx < run.length) total += weighPlain(run.slice(idx));
        return total;
    }

    /* Weight of text containing neither URLs nor emoji: per codepoint. */
    function weighPlain(s) {
        var total = 0;
        for (var i = 0; i < s.length; i++) {
            var cp = s.codePointAt(i);
            if (cp > 0xFFFF) i++;                    // surrogate pair: one codepoint
            total += codepointWeight(cp);
        }
        return total;
    }

    /*
     * weightedLength(text) -> integer units, where the limit is 280.
     */
    function weightedLength(text) {
        if (!text) return 0;
        var total = 0;
        var last = 0;

        URL_RE.lastIndex = 0;
        var m;
        while ((m = URL_RE.exec(text)) !== null) {
            // The bare-host alternative may capture a leading space or "(" —
            // that character is ordinary text and must still be weighed.
            var matched = m[0];
            var lead = matched.match(/^[\s(]/) ? 1 : 0;
            var urlStart = m.index + lead;

            if (urlStart > last) total += weighRun(text.slice(last, urlStart));
            total += CFG.transformedURLLength * CFG.scale;
            last = m.index + matched.length;
        }
        if (last < text.length) total += weighRun(text.slice(last));

        return Math.floor(total / CFG.scale);
    }

    function remaining(text) {
        return CFG.maxWeightedLength - weightedLength(text);
    }

    function isOverLimit(text) {
        return weightedLength(text) > CFG.maxWeightedLength;
    }

    /* =====================================================================
     * 2. Engagement count abbreviation
     * =====================================================================
     *
     * X's actual rule, which is TRUNCATION not rounding — 1,999 renders as
     * "1.9K" and never "2K". Rounding here is the tell that a screenshot is
     * fake, because the real client never produces "2.0K".
     *
     *      0 – 999          exact, with locale grouping        "999"
     *    1,000 – 9,999      one decimal, truncated, .0 dropped "1.2K" "1K"
     *   10,000 – 999,999    no decimal, truncated              "10K" "999K"
     *   1,000,000+          one decimal below 10M, then none   "1.2M" "10M"
     *
     * `locale` only affects the thousands separator on the exact branch and
     * the decimal mark; the K/M suffixes are what the X client renders in
     * every locale we ship.
     */
    function abbreviateCount(n, locale) {
        n = Number(n);
        if (!isFinite(n)) return '0';
        var neg = n < 0;
        n = Math.floor(Math.abs(n));
        var out;

        if (n < 1000) {
            out = groupDigits(n, locale);
        } else if (n < 10000) {
            out = truncDecimal(n / 1000, locale) + 'K';
        } else if (n < 1000000) {
            out = groupDigits(Math.floor(n / 1000), locale) + 'K';
        } else if (n < 10000000) {
            out = truncDecimal(n / 1000000, locale) + 'M';
        } else if (n < 1000000000) {
            out = groupDigits(Math.floor(n / 1000000), locale) + 'M';
        } else {
            out = truncDecimal(n / 1000000000, locale) + 'B';
        }
        return (neg ? '-' : '') + out;
    }

    /* One decimal place, TRUNCATED, with a trailing ".0" dropped entirely. */
    function truncDecimal(v, locale) {
        var t = Math.floor(v * 10) / 10;
        var whole = Math.floor(t);
        var tenth = Math.round((t - whole) * 10);
        if (tenth === 0) return groupDigits(whole, locale);
        return groupDigits(whole, locale) + decimalMark(locale) + tenth;
    }

    function decimalMark(locale) {
        try {
            return (1.1).toLocaleString(locale || 'en').replace(/\d/g, '') || '.';
        } catch (e) { return '.'; }
    }

    function groupDigits(n, locale) {
        try {
            return Number(n).toLocaleString(locale || 'en');
        } catch (e) {
            return String(n);
        }
    }

    /* =====================================================================
     * 3. Timestamps
     * =====================================================================
     *
     * Two different formats, and using the wrong one for the layout is the
     * most common giveaway in the category:
     *
     *   FEED (timeline)  relative and terse — "12s" "34m" "5h" "Mar 14"
     *                    and "Mar 14, 2024" once it is not this year.
     *   DETAIL (single)  absolute and long  — "9:41 AM · Mar 14, 2024"
     *
     * `now` is injected rather than read from the clock so this is a pure
     * function and the tests are not time-dependent.
     */
    function relativeTimestamp(then, now, locale) {
        var t = toDate(then), n = toDate(now) || new Date();
        if (!t) return '';
        var secs = Math.floor((n - t) / 1000);
        if (secs < 0) secs = 0;

        if (secs < 60) return secs + 's';
        var mins = Math.floor(secs / 60);
        if (mins < 60) return mins + 'm';
        var hrs = Math.floor(mins / 60);
        if (hrs < 24) return hrs + 'h';

        var sameYear = t.getFullYear() === n.getFullYear();
        return fmtDate(t, locale, sameYear);
    }

    function absoluteTimestamp(then, locale) {
        var t = toDate(then);
        if (!t) return '';
        return fmtTime(t, locale) + ' · ' + fmtDate(t, locale, false);
    }

    function fmtDate(d, locale, omitYear) {
        var opts = omitYear
            ? { month: 'short', day: 'numeric' }
            : { month: 'short', day: 'numeric', year: 'numeric' };
        try {
            return new Intl.DateTimeFormat(locale || 'en', opts).format(d);
        } catch (e) {
            return d.toDateString();
        }
    }

    function fmtTime(d, locale) {
        try {
            return new Intl.DateTimeFormat(locale || 'en', {
                hour: 'numeric', minute: '2-digit'
            }).format(d);
        } catch (e) {
            return d.toTimeString().slice(0, 5);
        }
    }

    function toDate(v) {
        if (v instanceof Date) return isNaN(v) ? null : v;
        if (typeof v === 'number') return new Date(v);
        if (typeof v === 'string' && v) {
            var d = new Date(v);
            return isNaN(d) ? null : d;
        }
        return null;
    }

    /* =====================================================================
     * 4. Handle and display-name normalisation
     * ===================================================================== */

    /*
     * X handles are 1–15 chars of [A-Za-z0-9_]. Anything else the user types
     * is stripped rather than rejected, because this is a mockup tool and
     * refusing input mid-typing is worse than quietly rendering what X would.
     */
    function normalizeHandle(raw) {
        if (!raw) return '';
        return String(raw).replace(/^@+/, '').replace(/[^A-Za-z0-9_]/g, '').slice(0, 15);
    }

    /* =====================================================================
     * 5. Post-text entity segmentation
     * =====================================================================
     *
     * Splits body text into plain runs and the entities X renders in its link
     * colour: @mentions, #hashtags, $cashtags and URLs. Returns DATA, never
     * markup — the caller escapes and builds nodes. Returning an HTML string
     * from here would put an injection sink in the one file with no DOM
     * access to defend it.
     *
     * A displayed URL is also shortened the way X shows it: scheme and "www."
     * dropped, path truncated with an ellipsis.
     */
    function segment(text) {
        if (!text) return [];
        var out = [];
        var pattern = new RegExp(
            URL_RE.source + '|(?:^|[^\\w])@([A-Za-z0-9_]{1,15})|(?:^|[^\\w])#([\\w\\u0400-\\u04FF\\u4E00-\\u9FFF]+)|(?:^|[^\\w])\\$([A-Za-z]{1,6})\\b',
            'gi'
        );
        var last = 0, m;
        while ((m = pattern.exec(text)) !== null) {
            var matched = m[0];
            var lead = matched.match(/^[^\w@#$h]/) && !/^https?:/i.test(matched) ? 1 : 0;
            // Re-find the true entity start so a captured leading space stays plain.
            var start = m.index + (matched.length - matched.replace(/^[\s(,.:;!?]/, '').length);
            if (start > last) out.push({ type: 'text', value: text.slice(last, start) });

            var body = text.slice(start, m.index + matched.length);
            if (/^(?:https?:\/\/|www\.)/i.test(body) || /^[a-z0-9-]+\.[a-z]{2,}/i.test(body)) {
                out.push({ type: 'url', value: body, display: displayUrl(body) });
            } else if (body.charAt(0) === '@') {
                out.push({ type: 'mention', value: body, handle: body.slice(1) });
            } else if (body.charAt(0) === '#') {
                out.push({ type: 'hashtag', value: body, tag: body.slice(1) });
            } else if (body.charAt(0) === '$') {
                out.push({ type: 'cashtag', value: body, symbol: body.slice(1) });
            } else {
                out.push({ type: 'text', value: body });
            }
            last = m.index + matched.length;
            if (matched.length === 0) pattern.lastIndex++;
        }
        if (last < text.length) out.push({ type: 'text', value: text.slice(last) });
        return out;
    }

    /* How X renders a link in the body: no scheme, no www., path clipped. */
    function displayUrl(u, max) {
        max = max || 30;
        var s = String(u).replace(/^https?:\/\//i, '').replace(/^www\./i, '');
        if (s.length <= max) return s;
        return s.slice(0, max - 1) + '…';
    }

    window.ITVXFormat = {
        CFG: CFG,
        weightedLength: weightedLength,
        remaining: remaining,
        isOverLimit: isOverLimit,
        abbreviateCount: abbreviateCount,
        relativeTimestamp: relativeTimestamp,
        absoluteTimestamp: absoluteTimestamp,
        normalizeHandle: normalizeHandle,
        segment: segment,
        displayUrl: displayUrl
    };

    // Node/test harness access without a DOM.
    if (typeof module !== 'undefined' && module.exports) {
        module.exports = window.ITVXFormat;
    }
})(typeof window !== 'undefined' ? window : globalThis);
